import React, { forwardRef, useImperativeHandle, useRef } from 'react';
import { SuggestionsList } from 'app/modules/textEditor/components/SuggestionsList';
import MentionSearchFailureNotice from 'app/modules/textEditor/components/MentionSearchFailureNotice';
import { useMentionSuggestions } from 'app/modules/textEditor/hooks/useMentionSuggestions';
import { SuggestionApi } from 'api/suggestions';
import type { CoreSuggestion, MentionSuggestion } from 'types/Suggestions';

interface Props {
  config: { pageId: number; pageType: number };
  query: string;
  command(editorMention: {
    label: string;
    id: string;
    link: string | null | undefined;
    role: string | undefined;
  }): void;
}

interface ListHandle {
  onKeyDown(props: { event: KeyboardEvent }): boolean;
}

const MentionSuggestions = forwardRef<ListHandle, Props>(
  ({ config, query, command }, ref) => {
    const state = useMentionSuggestions({ ...config, query });
    const listRef = useRef<ListHandle | null>(null);

    const reportSelection = (
      item: CoreSuggestion & Partial<MentionSuggestion>,
    ) => {
      // `SuggestionsList.onSelect`'s item type is shared with the
      // user-mention path, which has no `kind` at all — so it is optional
      // here even though this handler needs it. Guard rather than assume:
      // a future caller wiring user-mention items into this same `onSelect`
      // must silently skip the report, not send `entity_kind: undefined`
      // (axios drops the key, the endpoint 422s, and the FE swallows it).
      if (!item.kind) {
        return;
      }

      // Reporting is best-effort telemetry; it must never be able to break
      // mention insertion, which has already completed by the time this
      // runs (`SuggestionsList.selectItem` calls `command` before
      // `onSelect`). `reportSelection` itself swallows rejections, but this
      // catch also guards against it throwing synchronously.
      try {
        SuggestionApi.reportSelection({
          pageId: config.pageId,
          entityKind: item.kind,
          entityId: item.id,
          entityName: item.name,
          source: 'search',
        });
      } catch {
        // Intentionally swallowed — see comment above.
      }
    };

    useImperativeHandle(ref, () => ({
      onKeyDown: (keyProps) => listRef.current?.onKeyDown(keyProps) ?? false,
    }));

    if (state.status === 'idle') {
      return null;
    }

    if (state.status === 'loading') {
      return <SuggestionsList items={[]} isLoading command={command} />;
    }

    if (state.status === 'empty') {
      return (
        <MentionSearchFailureNotice
          variant="empty"
          query={state.query}
          notice={state.notice}
          partialReason={state.partialReason}
          pageType={config.pageType}
        />
      );
    }

    if (state.status === 'failed') {
      return (
        <MentionSearchFailureNotice
          variant="failure"
          failure={state.failure}
          pageType={config.pageType}
        />
      );
    }

    if (state.status === 'transportError') {
      return <MentionSearchFailureNotice variant="transportError" />;
    }

    return (
      <>
        <SuggestionsList
          ref={listRef}
          items={state.items}
          isLoading={false}
          command={command}
          onSelect={reportSelection}
          pageType={config.pageType}
        />
        {state.status === 'partial' && (
          <div
            className="tw-mt-1 tw-rounded-lg tw-bg-white tw-p-2 tw-text-sm tw-text-grayscale-180 tw-shadow-xl"
            data-cy="mention-suggestions_partial-notice"
            data-name="mention-suggestions_partial-notice"
          >
            {state.notice}
          </div>
        )}
      </>
    );
  },
);

export default MentionSuggestions;
